import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';

/**
 * Auth Interceptor - Adds authorization header and handles auth errors
 */
export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Skip auth header for public endpoints
  const publicEndpoints = [
    '/login',
    '/register',
    '/forgot-password',
    '/reset-password',
    '/verify-otp',
    '/resend-otp',
    '/check-email',
    // '/logout',
    // '/sessions/logout-others'
  ];

  const isPublicEndpoint = publicEndpoints.some(endpoint =>
    req.url.includes(endpoint)
  );

  const unsafeMethods = ['POST', 'PUT', 'PATCH', 'DELETE'];
  const needsCsrfHeader = unsafeMethods.includes(req.method) && !isPublicEndpoint;
  const missingCsrf = needsCsrfHeader && !authService.getCsrfToken();

  const send = (): Observable<HttpEvent<unknown>> => {
    let authReq = req.clone({
      withCredentials: true
    });

    const csrfToken = authService.getCsrfToken();
    if (csrfToken && needsCsrfHeader) {
      authReq = authReq.clone({
        setHeaders: {
          'x-csrf-token': csrfToken
        }
      });
    }

    return next(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && !isPublicEndpoint) {
          return handle401Error(authReq, next, authService, router);
        }

        if (error.status === 403) {
          return handle403Error(error, router);
        }

        return throwError(() => error);
      })
    );
  };

  // Cookie session stays on all requests (withCredentials). Unsafe methods wait for CSRF
  // so headers match server expectations without blocking initial Angular bootstrap.
  if (missingCsrf) {
    return from(authService.ensureCsrfReady()).pipe(switchMap(() => send()));
  }

  return send();
};

// Bearer token logic removed in favor of cookie-based auth

/**
 * Handle 401 Unauthorized error
 * Redirects to login and clears local storage
 */
function handle401Error(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router
): Observable<HttpEvent<unknown>> {
  authService.logout().subscribe({
    next: () => {
      router.navigate(['/']);
    },
    error: () => {
      router.navigate(['/']);
    }
  });
  return throwError(() => new Error('Session expired. Please login again.'));
}

/**
 * Handle 403 Forbidden error
 */
function handle403Error(
  error: HttpErrorResponse,
  router: Router
): Observable<never> {
  // User doesn't have permission
  router.navigate(['/unauthorized']); // need to work on this
  return throwError(() => error);
}
